// Library Methods
import { useTranslation } from "react-i18next";
import { useContext, useMemo } from "react";

// Constants
import { DEFAULT_DECIBEL_VALUE_FULL_120 } from "src/constants/enums";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import useExamReportStats from "src/hooks/reports/useExamReportStats";

// Utils
import {
  getReportEyeText,
  getTutorialCompletionText,
} from "src/utils/exams/examReportsHelper";
import { getLocalDateTimeWithZone } from "src/utils/date/dateHelper";

const useFull120Columns = () => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { eyeType, duration, examReport } = useContext(ReportSectionContext);

  const {
    fixationLossMethod,
    fixationLossPercentage,
    falsePositivePercentage,
    falseNegative,
  } = useExamReportStats(examReport);

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(
            examReport.timing.start,
            clinicInfo?.timezone
          )
        : { date: "N/A", time: "N/A" },
    [examReport?.timing?.start, clinicInfo?.timezone]
  );

  const full120Columns = useMemo(() => {
    const firstColumn = [
      {
        title: t("Eye"),
        value: getReportEyeText(eyeType, t),
        isBlueText: true,
      },
      {
        title: t("Fixation Method"),
        value: fixationLossMethod,
        isBlueText: true,
      },
      {
        title: t("Binocular Fixation Loss"),
        value: t("False"),
        isBlueText: true,
      },
      {
        title: t("Fixation Loss"),
        value: fixationLossPercentage,
        isBlueText: true,
      },
      {
        title: t("False Positives"),
        value: falsePositivePercentage,
        isBlueText: true,
      },
      {
        title: t("False Negatives"),
        value: falseNegative,
        isBlueText: true,
      },
    ];

    const secondColumn = [
      {
        title: t("Speed Type"),
        value: examReport?.exam?.speedType
          ? t(examReport?.exam?.speedType)
          : t("Normal"),
        isBlueText: true,
      },
      {
        title: t("Stimulus"),
        value: examReport?.exam?.stimulusSize,
        isBlueText: true,
      },
      {
        title: t("Stimulus Intensity"),
        value: `${examReport?.exam?.decibelValue ?? DEFAULT_DECIBEL_VALUE_FULL_120} dB`,
        isBlueText: true,
      },
      {
        title: t("Test Mode"),
        value: t("Single Intensity"),
        isBlueText: true,
      },
      {
        title: t("Fovea Measurement"),
        value:
          typeof examReport?.postProcessing?.foveaDb === "number"
            ? `${examReport?.postProcessing?.foveaDb} dB`
            : t("Off"),
        isBlueText: true,
      },
      {
        title: t("Tutorial"),
        value: getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ),
        isBlueText: false,
      },
    ];

    const thirdColumn = [
      { title: t("Date"), value: localDateAndTimeWithZone?.date },
      { title: t("Time_hour"), value: localDateAndTimeWithZone?.time },
      {
        title: t("Duration"),
        value: duration,
        isBlueText: true,
      },
    ];

    return { firstColumn, secondColumn, thirdColumn };
  }, [
    examReport?.postProcessing?.foveaDb,
    examReport?.exam?.speedType,
    examReport?.exam?.stimulusSize,
    examReport?.exam?.decibelValue,
    examReport?.postProcessing?.tutorialCompletionState,
    localDateAndTimeWithZone,
    eyeType,
    duration,
    fixationLossMethod,
    fixationLossPercentage,
    falsePositivePercentage,
    falseNegative,
    t,
  ]);

  return { full120Columns };
};

export default useFull120Columns;
